import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../services/api';
import type { BookingPackage } from '../services/types';

import { PageHeader } from '../ui/PageHeader';
import { Card } from '../ui/Card';

export default function BookingPage() {
  const { packageId } = useParams();
  const [pkg, setPkg] = React.useState<BookingPackage | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({ name: '', email: '', seats: 1 });

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      if (!packageId) {
        setError('Missing package id');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await api.getPackageById(packageId);
        if (!mounted) return;
        setPkg(res);
        setError(null);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load package');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [packageId]);

  const remainingSeats = pkg ? pkg.totalSeats - pkg.bookedSeats : 0;
  const total = pkg ? form.seats * pkg.basePricePerSeat : 0;

  const submitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pkg) return;

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      const res = await api.createBooking({
        packageId: pkg.id,
        name: form.name.trim(),
        email: form.email.trim(),
        seats: form.seats,
      });
      const refreshed = await api.getPackageById(pkg.id);
      setPkg({ ...refreshed });
      setSuccess(res.voucherCode ? `Booked. Voucher issued: ${res.voucherCode}` : 'Booked successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page heroPage">
      <div className="container">
        <PageHeader
          kicker="Booking"
          title={pkg ? pkg.title : 'Trip booking'}
          subtitle="Reserve seats for a community-led journey and see your price before confirming."
        />

        {loading && <div className="muted">Loading booking...</div>}
        {!loading && error && <div className="badge badgeWarn">{error}</div>}

        {!loading && !pkg && (
          <div style={{ marginTop: 16 }}>
            <Link className="btn" to="/agents">
              Back to trips
            </Link>
          </div>
        )}

        {!loading && pkg && (
          <div className="grid cols2" style={{ alignItems: 'start' }}>
            <Card title="Trip details">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <img className="mediaImage mediaImageTall" src={pkg.imageUrl} alt={pkg.title} />
                <div>
                  <div className="label">Operator</div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    {pkg.operatorName}
                  </div>
                </div>
                <div>
                  <div className="label">Destination</div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    {pkg.destination}
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <span className="badge">
                    {pkg.startDate} to {pkg.endDate}
                  </span>
                  <span className="badge badgeAccent">{remainingSeats} seats left</span>
                  <span className="badge">${pkg.basePricePerSeat} per seat</span>
                </div>
                {remainingSeats === 1 && (
                  <div className="badge badgeWarn">
                    Last seat bookings receive a {Math.round(pkg.discountForLastSeat * 100)}% voucher.
                  </div>
                )}
              </div>
            </Card>

            <Card title="Reserve seats">
              <form className="formRow" onSubmit={submitBooking}>
                <label>
                  <div className="label">Name</div>
                  <input
                    className="input"
                    required
                    value={form.name}
                    onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                  />
                </label>
                <label>
                  <div className="label">Email</div>
                  <input
                    className="input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))}
                  />
                </label>
                <label>
                  <div className="label">Seats</div>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={Math.max(1, remainingSeats)}
                    value={form.seats}
                    onChange={(e) =>
                      setForm((current) => ({ ...current, seats: Math.max(1, Number(e.target.value) || 1) }))
                    }
                  />
                </label>

                <div className="card" style={{ boxShadow: 'none' }}>
                  <div className="label">Estimated total</div>
                  <div style={{ fontSize: 28, fontWeight: 900, marginTop: 4 }}>${total}</div>
                </div>

                {success && <div className="badge badgeAccent">{success}</div>}
                {error && <div className="badge badgeWarn">{error}</div>}

                <button className="btn btnPrimary" type="submit" disabled={submitting || remainingSeats === 0}>
                  {submitting ? 'Booking...' : 'Confirm booking'}
                </button>
              </form>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
